import { makeAutoObservable } from "mobx";
import { Price, } from "../ordering";
import { PPrice } from "./PPrice";
import { Percentage, Money } from "../core";
import { t, translate } from "../I18n";
const DEFAULT_PERCENTAGE_VALUES = [10, 15, 20];
export class PTip {
    constructor(params) {
        this.localizationProvider = params.localizationProvider;
        this.domain = params.domain;
        this.purchaseOrderPriceService = params.purchaseOrderPriceService;
        makeAutoObservable(this);
    }
    static create(params) {
        switch (params.domain.kind) {
            case "Enabled":
                return new PTip({
                    localizationProvider: params.localizationProvider,
                    domain: params.domain,
                    purchaseOrderPriceService: params.purchaseOrderPriceService,
                });
            case "Disabled":
                return null;
        }
    }
    get description() {
        switch (this.domain.description.tag) {
            case "DefaultDescription":
                return translate(t.formtastic.labels.order.tip_label);
            case "CustomDescription":
                return this.localizationProvider.localize(this.domain.description.text);
        }
    }
    get options() {
        const { priceWithoutTip } = this.purchaseOrderPriceService;
        switch (this.domain.valueType.tag) {
            case "Default": {
                return getOptionsFromPercentageValues(DEFAULT_PERCENTAGE_VALUES, priceWithoutTip, this.localizationProvider);
            }
            case "CustomPercentage": {
                const percentageValues = [
                    this.domain.valueType.percentage1,
                    this.domain.valueType.percentage2,
                    this.domain.valueType.percentage3,
                ];
                return getOptionsFromPercentageValues(percentageValues, priceWithoutTip, this.localizationProvider);
            }
            case "CustomAmount": {
                const amountValues = [
                    this.domain.valueType.amount1,
                    this.domain.valueType.amount2,
                    this.domain.valueType.amount3,
                ];
                return getOptionsFromAmountValues(amountValues, this.localizationProvider);
            }
        }
    }
    get numericValue() {
        return this.domain.numericValue;
    }
    get priceValue() {
        return this.localizationProvider.formatPrice(this.domain.value);
    }
    get currencySymbol() {
        return this.localizationProvider.currencySymbol;
    }
    get currencySeparator() {
        return this.localizationProvider.currencySeparator;
    }
    get currencyPrecision() {
        return this.localizationProvider.currencyPrecision;
    }
    getOrderPriceWithTip(tipValue) {
        const { priceWithoutTip } = this.purchaseOrderPriceService;
        const tipPrice = Price.unscoped(Money.fromNumber(tipValue !== null && tipValue !== void 0 ? tipValue : 0, "monetary"));
        const orderPriceWithTip = tipPrice.finalValue === null || tipPrice.finalValue.leq(Money.zero())
            ? priceWithoutTip
            : priceWithoutTip.add(tipPrice);
        return new PPrice(this.localizationProvider, orderPriceWithTip.spectrum)
            .priceValue;
    }
    onChange(value) {
        this.domain.setValue(Money.fromNumber(value !== null && value !== void 0 ? value : 0, "monetary"));
    }
    onRemove() {
        this.domain.resetValue();
    }
}
function getOptionsFromPercentageValues(percentageValues, orderPriceWithoutTip, localizationProvider) {
    return percentageValues.map((percentageValue) => {
        var _a, _b;
        const percentage = Percentage.fromNumber(percentageValue);
        const price = getPercentagePrice(percentage, orderPriceWithoutTip);
        return {
            label: new PPrice(localizationProvider, price.spectrum).priceValue,
            numericValue: (_b = (_a = price.finalValue) === null || _a === void 0 ? void 0 : _a.asNumber) !== null && _b !== void 0 ? _b : null,
            description: percentage.toString(),
        };
    });
}
function getPercentagePrice(percentage, orderPriceWithoutTip) {
    const tipPrice = orderPriceWithoutTip.percentage(percentage);
    const orderPriceWithTip = tipPrice.finalValue === null || tipPrice.finalValue.leq(Money.zero())
        ? orderPriceWithoutTip
        : orderPriceWithoutTip.add(tipPrice);
    const orderPriceWithTipRounded = orderPriceWithTip.roundUpTo50Cents();
    return orderPriceWithTipRounded.sub(orderPriceWithoutTip);
}
function getOptionsFromAmountValues(amountValues, localizationProvider) {
    return amountValues.map((amountValue) => {
        var _a;
        const priceValue = Money.fromBackend(amountValue);
        return {
            label: localizationProvider.formatPrice(priceValue),
            numericValue: (_a = priceValue.asNumber) !== null && _a !== void 0 ? _a : null,
            description: null,
        };
    });
}
